import { Processor } from '@/utils/processor';
import { formatFontFamilyName } from '@/utils/text-kit';
import type { FontLibrary } from '../library';
import { type FontProcessor } from './worker/font-processor';
import workerUrl from './worker/font-processor?worker&url';

/** 复用历史字体 worker 链路，在 worker 内下载全量字体。 */
const fontProcessor = new Processor();
let fontProcessorInitialized = false;

/** document 字体属于页面级资源，不同 FullFontFaceManager 实例共享状态。 */
const loadedDocumentFonts = new Set<string>();
const loadingDocumentFonts = new Map<string, Promise<string | null>>();

const getFontProcessor = () => {
    if (!fontProcessorInitialized) {
        fontProcessorInitialized = true;
        try {
            const worker = new Worker(new URL('./worker/font-processor', import.meta.url), { type: 'module' });
            // module worker 加载失败时会异步触发 error；高版本 Safari 跨域场景改走 Blob worker。
            worker.addEventListener('error', () => {
                fontProcessor.clearWorker();
                fontProcessor.setUrl(workerUrl);
            });
            fontProcessor.setWorker(worker);
        } catch (_) {
            fontProcessor.setUrl(workerUrl);
        }
    }

    return fontProcessor.get<typeof FontProcessor>();
};

/**
 * 管理注册到 document 的全量字体。
 *
 * - 使用历史 Processor + font-processor worker 链路加载 WOFF2；
 * - 检查 document.fonts 中已加载的全量字体；
 * - 跨 FullFontFaceManager 实例合并同一 font-family 的在途请求。
 */
export class FullFontFaceManager {
    constructor(private readonly library: FontLibrary) {}

    /** 检查 document 中是否已有加载完成的全量字体。 */
    public getLoadedFamily(fontFamily: string): string | null {
        const normalizedFamily = formatFontFamilyName(fontFamily);
        if (loadedDocumentFonts.has(normalizedFamily)) return normalizedFamily;
        if (typeof document === 'undefined' || !document.fonts) return null;

        let loaded = false;
        document.fonts.forEach((fontFace) => {
            if (
                formatFontFamilyName(fontFace.family.replace(/^['"]|['"]$/g, '')) === normalizedFamily &&
                fontFace.status === 'loaded'
            ) {
                loaded = true;
            }
        });
        if (!loaded) return null;

        loadedDocumentFonts.add(normalizedFamily);
        return normalizedFamily;
    }

    /** 后台发起全量字体加载；调用方不等待该任务。 */
    public preload(fontFamily: string): void {
        void this.load(fontFamily).catch(() => undefined);
    }

    /** 使用 worker 加载并注册全量字体，资源格式优先 WOFF2、缺失时回退 TTF。 */
    public load(fontFamily: string): Promise<string | null> {
        const loadedFontFamily = this.getLoadedFamily(fontFamily);
        if (loadedFontFamily) return Promise.resolve(loadedFontFamily);

        const normalizedFamily = formatFontFamilyName(fontFamily);
        const loading = loadingDocumentFonts.get(normalizedFamily);
        if (loading) return loading;

        const source = this.library.resolveSource(fontFamily, ['woff2', 'ttf']);
        if (!source || typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
            return Promise.resolve(null);
        }

        const promise = getFontProcessor()
            .then((processor) => processor.loadFont(source.url))
            .then(async ({ buffer }) => {
                const fontFace = new FontFace(normalizedFamily, buffer, { display: 'swap' });
                document.fonts.add(fontFace);
                await fontFace.load();
                loadedDocumentFonts.add(normalizedFamily);
                return normalizedFamily;
            })
            .finally(() => {
                if (loadingDocumentFonts.get(normalizedFamily) === promise) {
                    loadingDocumentFonts.delete(normalizedFamily);
                }
            });

        loadingDocumentFonts.set(normalizedFamily, promise);
        return promise;
    }
}
